// ---------------------------------------------------------------------------
// VersionsRail — reusable right-hand panel that lists a versioned entity's
// immutable snapshots (agents, graders, evaluation templates). Each row shows
// the version number + creation date. When an `onViewVersion` handler is
// provided the rows become clickable and open a read-only preview (owned by the
// parent); otherwise they are display-only. Presentational — the parent owns
// fetching and the dismiss action.
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Dismiss20Filled,
  Dismiss20Regular,
  bundleIcon,
} from '@fluentui/react-icons';

import { useStyles } from './VersionsRail.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const DismissIcon = bundleIcon(Dismiss20Filled, Dismiss20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

/** Minimal shape every versioned entity's snapshot satisfies. */
export interface VersionRowData {
  version: number;
  systemProperties: { createdDate: string };
}

function formatVersionDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface VersionsRailProps<T extends VersionRowData> {
  versions: T[];
  loading: boolean;
  error: string | null;
  onHide: () => void;
  /** When provided, rows are clickable and invoke this with the full version. */
  onViewVersion?: (version: T) => void;
}

export function VersionsRail<T extends VersionRowData>({
  versions,
  loading,
  error,
  onHide,
  onViewVersion,
}: VersionsRailProps<T>) {
  const styles = useStyles();

  return (
    <aside className={styles.versionsColumn} aria-label="Version history">
      <div className={styles.versionsHeader}>
        <span className={styles.versionsTitle}>Versions</span>
        <div className={styles.versionsHeaderRight}>
          {versions.length > 0 && (
            <span className={styles.versionsCount}>
              {String(versions.length)} total
            </span>
          )}
          <Tooltip content="Hide version history" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<DismissIcon />}
              aria-label="Hide version history"
              onClick={onHide}
            />
          </Tooltip>
        </div>
      </div>
      {loading && versions.length === 0 ? (
        <div className={styles.versionsEmpty}>Loading versions…</div>
      ) : error !== null ? (
        <div className={styles.versionsEmpty}>
          <Body1 className={styles.errorText} role="alert">
            {error}
          </Body1>
        </div>
      ) : versions.length === 0 ? (
        <div className={styles.versionsEmpty}>No version snapshots yet.</div>
      ) : (
        <div className={styles.versionsList}>
          {versions.map((v) =>
            onViewVersion ? (
              <button
                type="button"
                key={v.version}
                className={mergeClasses(
                  styles.versionRow,
                  styles.versionRowInteractive,
                )}
                onClick={() => onViewVersion(v)}
                aria-label={`View version ${String(v.version)}`}
              >
                <span className={styles.versionNumber}>v{v.version}</span>
                <span className={styles.versionDate}>
                  {formatVersionDate(v.systemProperties.createdDate)}
                </span>
              </button>
            ) : (
              <div key={v.version} className={styles.versionRow}>
                <span className={styles.versionNumber}>v{v.version}</span>
                <span className={styles.versionDate}>
                  {formatVersionDate(v.systemProperties.createdDate)}
                </span>
              </div>
            ),
          )}
        </div>
      )}
    </aside>
  );
}
